import hierarchyService from '../services/hierarchy'
import axios from 'axios'

// State
export const state = {
  // Hierarchy tree data
  hierarchyTree: [],
  isLoadingTree: false,
  hierarchyTreeCancelToken: null, // Add cancel token for hierarchy tree requests

  // Dropdown options for NewClassificationModal
  dropdownOptions: {
    brands: [],
    subBrands: [],
    departments: [],
    categories: []
  },
  isLoadingDropdownOptions: false,

  // Error handling
  error: null
}

// Getters
export const getters = {
  hierarchyTree: state => state.hierarchyTree,
  isLoadingTree: state => state.isLoadingTree,

  // Dropdown options getters
  dropdownOptions: state => state.dropdownOptions,
  brandOptions: state => state.dropdownOptions.brands,
  subBrandOptions: state => state.dropdownOptions.subBrands,
  departmentOptions: state => state.dropdownOptions.departments,
  categoryOptions: state => state.dropdownOptions.categories,
  isLoadingDropdownOptions: state => state.isLoadingDropdownOptions,

  error: state => state.error
}

// Mutations
export const mutations = {
  // Hierarchy tree mutations
  SET_HIERARCHY_TREE(state, tree) {
    state.hierarchyTree = tree
  },

  SET_LOADING_TREE(state, loading) {
    state.isLoadingTree = loading
  },

  SET_HIERARCHY_TREE_CANCEL_TOKEN(state, cancelToken) {
    state.hierarchyTreeCancelToken = cancelToken
  },

  // Dropdown options mutations
  SET_DROPDOWN_OPTIONS(state, options) {
    state.dropdownOptions = options
  },

  SET_LOADING_DROPDOWN_OPTIONS(state, loading) {
    state.isLoadingDropdownOptions = loading
  },

  // Error handling
  SET_ERROR(state, error) {
    state.error = error
  },

  CLEAR_ERROR(state) {
    state.error = null
  }
}

// Actions
export const actions = {
  /**
   * Fetch hierarchy tree for brands page navigation
   */
  async fetchHierarchyTree({ commit, state }) {
    // Cancel any existing request
    if (state.hierarchyTreeCancelToken) {
      state.hierarchyTreeCancelToken.cancel('New hierarchy tree request initiated')
    }

    // Create new cancel token
    const cancelToken = axios.CancelToken.source()
    commit('SET_HIERARCHY_TREE_CANCEL_TOKEN', cancelToken)

    commit('SET_LOADING_TREE', true)
    commit('CLEAR_ERROR')

    try {
      const response = await hierarchyService.getBrandsTree(cancelToken.token)
      commit('SET_HIERARCHY_TREE', response.data.data.brands)
    } catch (error) {
      // Don't throw error for cancelled requests
      if (!axios.isCancel(error)) {
        commit('SET_HIERARCHY_TREE', [])
        throw error
      }
    } finally {
      commit('SET_LOADING_TREE', false)
      commit('SET_HIERARCHY_TREE_CANCEL_TOKEN', null)
    }
  },

  /**
   * Cancel pending hierarchy tree request
   */
  cancelHierarchyTreeRequest({ state, commit }) {
    if (state.hierarchyTreeCancelToken) {
      state.hierarchyTreeCancelToken.cancel('Request cancelled by user')
      commit('SET_HIERARCHY_TREE_CANCEL_TOKEN', null)
    }
  },

  /**
   * Fetch dropdown options for NewClassificationModal
   */
  async fetchDropdownOptions({ commit }) {
    commit('SET_LOADING_DROPDOWN_OPTIONS', true)
    commit('CLEAR_ERROR')

    try {
      const response = await hierarchyService.getDropdownOptions()
      commit('SET_DROPDOWN_OPTIONS', response.data.data)
    } catch (error) {
      commit('SET_DROPDOWN_OPTIONS', {
        brands: [],
        subBrands: [],
        departments: [],
        categories: []
      })
      throw error
    } finally {
      commit('SET_LOADING_DROPDOWN_OPTIONS', false)
    }
  },

  /**
   * Create new hierarchy combination
   */
  async createHierarchy({ commit, dispatch }, payload) {
    commit('CLEAR_ERROR')

    try {
      const response = await hierarchyService.createHierarchy(payload)
      // Refresh hierarchy tree after creation
      await dispatch('fetchHierarchyTree')
      return response.data
    } catch (error) {
      throw error
    }
  },

  /**
   * Update hierarchy entity
   */
  async updateHierarchy({ commit, dispatch }, { id, payload }) {
    commit('CLEAR_ERROR')

    try {
      const response = await hierarchyService.updateHierarchy(id, payload)

      if (response.data.status) {
        // Refresh hierarchy tree after update
        await dispatch('fetchHierarchyTree')
        return response.data
      } else {
        throw new Error(response.data.message || 'Failed to update hierarchy')
      }
    } catch (error) {
      console.error('Error updating hierarchy:', error)
      commit('SET_ERROR', error.response?.data?.message || error.message || 'Failed to update hierarchy')
      throw error
    }
  },

  /**
   * Delete hierarchy entity
   */
  async deleteHierarchy({ commit, dispatch }, { id, payload }) {
    commit('CLEAR_ERROR')

    try {
      const response = await hierarchyService.deleteHierarchy(id, payload)

      if (response.data.status) {
        // Refresh hierarchy tree after deletion
        await dispatch('fetchHierarchyTree')
        return response.data
      } else {
        throw new Error(response.data.message || 'Failed to delete hierarchy')
      }
    } catch (error) {
      console.error('Error deleting hierarchy:', error)
      commit('SET_ERROR', error.response?.data?.message || error.message || 'Failed to delete hierarchy')
      throw error
    }
  },

  /**
   * Copy products to new hierarchy
   */
  async copyProducts({ commit, dispatch }, payload) {
    commit('CLEAR_ERROR')

    try {
      const response = await hierarchyService.copyProducts(payload)

      if (response.data.status) {
        // Refresh current product list
        return response.data
      } else {
        throw new Error(response.data.message || 'Failed to copy products')
      }
    } catch (error) {
      console.error('Error copying products:', error)
      commit('SET_ERROR', error.response?.data?.message || error.message || 'Failed to copy products')
      throw error
    }
  },

  /**
   * Move products to new hierarchy
   */
  async moveProducts({ commit, dispatch }, payload) {
    commit('CLEAR_ERROR')

    try {
      const response = await hierarchyService.moveProducts(payload)

      if (response.data.status) {
        return response.data
      } else {
        throw new Error(response.data.message || 'Failed to move products')
      }
    } catch (error) {
      console.error('Error moving products:', error)
      commit('SET_ERROR', error.response?.data?.message || error.message || 'Failed to move products')
      throw error
    }
  },

  /**
   * Clear error state
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR')
  }
}
